import { getApiBase } from '@/lib/deployment'

const TOKEN_KEY = 'aip_dns_access_token'
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
// API base is computed at runtime so the same bundle works at both
// localhost:3000/ui/ and behind a reverse proxy at /<prefix>/ui/.
const API_BASE = getApiBase()

// ─── Type definitions ────────────────────────────────────────────────────────

export interface HealthResponse {
  status: string
  service: string
  version: string
}

export interface DNSRecord {
  record_id: string
  name: string
  record_type: string
  value: string
  ttl: number
  zone: string
  zone_id: string
  zone_name: string
  status: string
  cloud_customer_id?: string | null
}

export interface DNSRecordCreate {
  name: string
  record_type: string
  value: string
  // Optional: multiple values for load balancing (e.g., multiple IPs)
  // If provided, takes precedence over 'value'
  values?: string[]
  ttl?: number
  zone: 'public' | 'private'
  // Owning tenant — embedded into the record name as
  // {name}.{cloud_customer_id}.{zone_domain}. Required on every create.
  cloud_customer_id: string
}

export interface DNSRecordUpdate {
  value?: string
  // Optional: multiple values for load balancing (e.g., multiple IPs)
  // If provided, takes precedence over 'value'
  values?: string[]
  ttl?: number
}

export interface DNSListResponse {
  records: DNSRecord[]
  total: number
  zone: string
  zone_id: string
}

export interface DNSHealthResponse {
  status: string
  zones: Record<string, string>
  message: string
}

export interface CertificateInfo {
  cert_id: string
  cert_name: string
  common_name?: string
  issue_date?: string
  expiry_date?: string
  status?: string
  cert_type?: number
  ownergroup?: string
  san_list?: string[]
}

export interface CertEnrollRequest {
  cert_type: number
  csr: string
  comments: string
  ownergroup: string
  requester_emails: string
  subj_alt_names?: string
  term?: number
}

export interface CertRenewRequest {
  cert_name: string
  cert_type: number
  csr: string
  comments: string
  ownergroup: string
  requester_emails: string
  subj_alt_names?: string
  term?: number
}

export interface CertRevokeRequest {
  cert_name: string
  reason: string
}

export interface CertCollectResponse {
  cert_id: string
  cert_name: string
  certificate?: string
  private_key?: string
  ca_bundle?: string
  full_chain?: string
}

export interface CertListResponse {
  certificates: CertificateInfo[]
  total: number
}

export interface MeResponse {
  ok: boolean
  auth_user: unknown
  is_admin: boolean
  // Present when the caller carries a cloud_customer_id claim. Admins
  // (TD-Operations) report their claim value here too.
  cloud_customer_id?: string | null
  // Customers have no certificate access — the UI hides the feature when false.
  cert_access: boolean
  userinfo?: Record<string, unknown> | null
}

export interface AuthInfoResponse {
  ok: boolean
  error?: string
  issuer?: string
  client_id?: string
  authorization_endpoint?: string
  device_authorization_endpoint?: string
  token_endpoint?: string
  jwks_uri?: string
  scopes?: string
}

export interface DeviceFlowStartResponse {
  ok: boolean
  verification_uri: string
  verification_uri_complete: string
  user_code: string
  device_code: string
  expires_in: number
  interval: number
  token_url: string
  device_authorization_endpoint: string
  client_id: string
}

export interface DeviceFlowPollResponse {
  status: 'ok' | 'pending' | 'slow_down' | 'expired_token' | 'access_denied' | 'error' | string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  detail?: string
}

// ─── HTTP client ─────────────────────────────────────────────────────────────

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = SKIP_AUTH ? null : localStorage.getItem(TOKEN_KEY)

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    if (!SKIP_AUTH) {
      window.dispatchEvent(new CustomEvent('aip-dns:auth-expired'))
      throw new Error('Authentication expired. Please log in again.')
    }
    // In SKIP_AUTH mode, 401s shouldn't redirect to login
    throw new Error(`HTTP ${response.status}`)
  }

  if (!response.ok) {
    let message = `HTTP ${response.status}`
    try {
      const data = await response.json()
      message = data.detail || data.message || message
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message)
  }

  // Handle no-content responses
  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

// ─── Identity ────────────────────────────────────────────────────────────────

export async function getMe(): Promise<MeResponse> {
  return apiFetch('/me')
}

// ─── Health & Status ─────────────────────────────────────────────────────────

export async function getHealth(): Promise<HealthResponse> {
  return apiFetch('/health')
}

export async function getDNSHealth(): Promise<DNSHealthResponse> {
  return apiFetch('/api/v1/dns/health')
}

export async function getCertHealth(): Promise<{ status: string; service: string; base_url: string }> {
  return apiFetch('/api/v1/certificates/health')
}

// ─── DNS Management ──────────────────────────────────────────────────────────

export async function listDNSRecords(
  zone: 'public' | 'private',
  recordType?: string,
  name?: string
): Promise<DNSListResponse> {
  const params = new URLSearchParams({ zone })
  if (recordType) params.append('record_type', recordType)
  if (name) params.append('name', name)
  return apiFetch(`/api/v1/dns/records?${params}`)
}

export async function getDNSRecord(
  zone: string,
  recordType: string,
  name: string
): Promise<DNSRecord> {
  return apiFetch(`/api/v1/dns/records/${zone}/${recordType}/${name}`)
}

export async function createDNSRecord(record: DNSRecordCreate): Promise<DNSRecord> {
  return apiFetch('/api/v1/dns/records', {
    method: 'POST',
    body: JSON.stringify(record),
  })
}

export async function updateDNSRecord(
  zone: string,
  recordType: string,
  name: string,
  update: DNSRecordUpdate
): Promise<DNSRecord> {
  return apiFetch(`/api/v1/dns/records/${zone}/${recordType}/${name}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  })
}

export async function deleteDNSRecord(
  zone: string,
  recordType: string,
  name: string
): Promise<{ success: boolean; message: string }> {
  return apiFetch(`/api/v1/dns/records/${zone}/${recordType}/${name}`, {
    method: 'DELETE',
  })
}

// ─── Certificate Management ──────────────────────────────────────────────────

export async function listCertificates(
  certName?: string,
  certId?: string
): Promise<CertListResponse> {
  const params = new URLSearchParams()
  if (certName) params.append('cert_name', certName)
  if (certId) params.append('cert_id', certId)
  return apiFetch(`/api/v1/certificates${params.toString() ? '?' + params.toString() : ''}`)
}

export async function enrollCertificate(request: CertEnrollRequest): Promise<any> {
  return apiFetch('/api/v1/certificates/enroll', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function renewCertificate(request: CertRenewRequest): Promise<any> {
  return apiFetch('/api/v1/certificates/renew', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function revokeCertificate(request: CertRevokeRequest): Promise<any> {
  return apiFetch('/api/v1/certificates/revoke', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function collectCertificate(
  certName?: string,
  certId?: string
): Promise<CertCollectResponse> {
  const params = new URLSearchParams()
  if (certName) params.append('cert_name', certName)
  if (certId) params.append('cert_id', certId)
  return apiFetch(`/api/v1/certificates/collect?${params}`)
}

// ─── Auth / IDP (unauthenticated /auth/* endpoints) ───────────────────────────

export async function getAuthInfo(): Promise<AuthInfoResponse> {
  const resp = await fetch(`${API_BASE}/auth/info`, {
    headers: { Accept: 'application/json' },
  })
  return resp.json() as Promise<AuthInfoResponse>
}

export async function startDeviceFlow(): Promise<DeviceFlowStartResponse> {
  const resp = await fetch(`${API_BASE}/auth/device/start`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })
  const data = await resp.json()
  if (!resp.ok) {
    throw new Error(data.detail || `Failed to start device flow (HTTP ${resp.status})`)
  }
  return data as DeviceFlowStartResponse
}

export async function pollDeviceFlow(
  deviceCode: string,
  tokenUrl: string,
  clientId: string
): Promise<DeviceFlowPollResponse> {
  const params = new URLSearchParams({
    device_code: deviceCode,
    token_url: tokenUrl,
    client_id: clientId,
  })
  const resp = await fetch(`${API_BASE}/auth/device/token?${params}`, {
    headers: { Accept: 'application/json' },
  })
  return resp.json() as Promise<DeviceFlowPollResponse>
}

// ─── DNS Resolution Testing ──────────────────────────────────────────────────

export interface DNSResolveRequest {
  hostname: string
  record_type?: 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX'
}

export interface DNSResolveResponse {
  hostname: string
  record_type: string
  resolved: boolean
  records: string[]
  error?: string | null
}

export async function resolveDNS(
  hostname: string,
  recordType: 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX' = 'A'
): Promise<DNSResolveResponse> {
  return apiFetch('/api/v1/dns/resolve', {
    method: 'POST',
    body: JSON.stringify({
      hostname,
      record_type: recordType,
    }),
  })
}
